import React from "react";
import styled from "styled-components";
import Link from "next/link";
import { Heading } from "../ui/Heading";
import { ApiImage } from "../ui/ApiImage";
import { useIsBreakpoint } from "~/hooks/useIsBreakpoint";
import { formatDate } from "~/utils";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import LinkOverlay from "../styled/LinkOverlay";
import TextWithArrow from "../ui/TextWithArrow";
import { themeImgSizes } from "~/theme";
import SafeHtmlSpan from "../ui/SafeHtmlSpan";
import DisplayAbove from "../styled/DisplayAbove";

const imgSizes = {
  desktop: "33.33vw",
  tablet: "50vw",
  base: "100vw",
};

const VideoTeaser = styled.article<{ inVerticalSlider?: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 0;
  padding-bottom: 56.25%;

  & .arrow {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.tablet} {
    & .arrow {
      display: block;
      opacity: 0;
      transition: opacity 0.3s;
    }

    @media (any-pointer: fine) {
      &:hover {
        .arrow {
          opacity: 1;
        }
      }
    }
  }

  ${({ theme }) => theme.breakpoints.tablet} {
    display: grid;
    grid-template-areas: "text img";
  }

  ${(props) =>
    props.theme.apply(["base", "mobile"], (breakpoint: string) => {
      return `
        padding-bottom: ${
          props.inVerticalSlider ? "calc(80vw / 16 * 9)" : "56.25%"
        };
        width: ${props.inVerticalSlider ? "80vw" : "100%"};
      `;
    })}

  ${(props) =>
    props.theme.apply(["tablet"], (breakpoint: string) => {
      return `
        padding-bottom: ${
          props.inVerticalSlider ? "calc(40vw / 16 * 9)" : "56.25%"
        };
        width: ${props.inVerticalSlider ? "40vw" : "100%"};
      `;
    })}

  ${(props) =>
    props.theme.apply(["tableLandscape", "dektop"], (breakpoint: string) => {
      return `
        padding-bottom: ${
          props.inVerticalSlider ? "calc(30vw / 16 * 9)" : "56.25%"
        };
        width: ${props.inVerticalSlider ? "30vw" : "100%"};
      `;
    })}
  
  ${(props) =>
    props.theme.apply(
      ["tableLandscape", "dektop", "screen"],
      (breakpoint: string) => {
        return `
        padding-bottom: ${
          props.inVerticalSlider ? "calc(28vw / 16 * 9)" : "56.25%"
        };
        width: ${props.inVerticalSlider ? "28vw" : "100%"};
      `;
      }
    )}
`;

const Img = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const Text = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  color: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5));
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        padding: ${props.theme.spacePx(breakpoint, 8)}  ${props.theme.spacePx(
        breakpoint,
        8
      )}  ${props.theme.spacePx(breakpoint, 9)}  ${props.theme.spacePx(
        breakpoint,
        8
      )};
      `;
    })}

  @media (any-pointer: fine) {
    &:hover {
      a {
        color: #fff;
      }
    }
  }
`;

const PostHeader = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr;

  ${({ theme }) => theme.breakpoints.tabletLandscape} {
    grid-template-columns: 1fr auto;
  }
`;

export const ListItemVideoOrAudio = ({
  item,
  inVerticalSlider,
  inCategoryListing,
}: {
  item: any;
  inVerticalSlider?: boolean;
  inCategoryListing?: boolean;
}) => {
  const { isBase, isMobile } = useIsBreakpoint();

  const config = useConfigContext();
  let type =
    item?.taxonomies?.media_category?.[0]?.slug === "featured" &&
    item?.taxonomies?.media_category?.[1]?.name
      ? item?.taxonomies?.media_category?.[1]?.name
      : item?.taxonomies?.media_category?.[0]?.name ?? item?.type ?? "";

  if (inVerticalSlider || inCategoryListing) {
    switch (item?.type) {
      case "audio_file":
        type = "Audio";
        break;

      case "podcast":
        type = "Podcast";
        break;

      default:
        type = "Video";
        break;
    }
  }

  return (
    <VideoTeaser inVerticalSlider={inVerticalSlider}>
      {item?.featuredImage?.sizes && (
        <Img>
          <ApiImage
            alt=""
            imgSizes={themeImgSizes(imgSizes)}
            sizes={item?.acf?.poster?.sizes ?? item?.featuredImage?.sizes}
            ratio="16 / 9"
            mode="fill"
            objectPosition={item?.acf?.poster?.cropPosition}
          />
        </Img>
      )}
      <Text>
        <PostHeader>
          <Heading heading="h4" space={10}>
            <SafeHtmlSpan html={type} />
            &emsp;
            <span style={{ fontWeight: 400 }}>
              <SafeHtmlSpan html={item?.acf?.duration} />
            </span>
          </Heading>

          <DisplayAbove breakpoint="tabletLandscape">
            <Heading heading="h4" space={10} className="arrow">
              <TextWithArrow
                align="right"
                heading="h4"
                size={9}
                filter="invert(100%)"
              >
                {item?.type === "audio_file" ? "Listen" : "Play"}
              </TextWithArrow>
            </Heading>
          </DisplayAbove>
        </PostHeader>
        <Link
          passHref
          href={`${config.baseUrl}/media/${
            item?.type === "audio_file" ? "audio" : "video"
          }/${item?.slug}`}
        >
          <LinkOverlay>
            <Heading
              heading={isBase || isMobile ? "h1" : "h2"}
              inline
              space={8}
              html={item?.title}
              maxLines={3}
            />
          </LinkOverlay>
        </Link>
      </Text>
    </VideoTeaser>
  );
};
export default ListItemVideoOrAudio;